/**
 * Web quầy (PWA cho tablet). Màn chính là **lịch hẹn hôm nay**, không phải dashboard số liệu:
 * việc lễ tân làm suốt ngày là xem ai đang làm gì và nhận khách mới.
 *
 * Ba trạng thái đăng nhập, và màn hình chỉ vẽ một trong ba:
 *   1. đang kiểm token cũ (mở lại tab thì không bắt gõ mã lại),
 *   2. chưa đăng nhập → màn đăng nhập,
 *   3. đã đăng nhập nhưng mã do chủ tiệm đặt hộ → **buộc** đổi mã, máy chủ chặn mọi việc khác.
 *
 * Khung (ADR-036): thanh bên trái trên tablet ngang, thanh dưới trên điện thoại — như Fresha
 * (docs/ui/THAM-KHAO.md §3.1). Menu theo VAI (phản ánh 25/09 — trước đây mọi vai chung một menu,
 * chỉ ẩn bớt mục, nên màn chính không nói được đâu là của chủ, của quầy, của thợ):
 *   - Kỹ thuật viên: vào thẳng "Việc của tôi" (việc hôm nay + hoa hồng), rồi "Lịch của tôi".
 *   - Lễ tân / Quản lý / Chủ tiệm: vào "Lịch hẹn" của cả tiệm; tiền, khách, cấu hình theo quyền.
 *   - Ai có nhận khách (được gán dịch vụ) mà không phải thợ: "Việc của tôi" ở menu tài khoản.
 * Nhãn vai luôn hiện ở mục tài khoản — người dùng biết mình đang đăng nhập với vai gì.
 */
import '@fontsource/be-vietnam-pro/400.css'
import '@fontsource/be-vietnam-pro/500.css'
import '@fontsource/be-vietnam-pro/600.css'
import '@fontsource/be-vietnam-pro/700.css'
import {
  CalendarDays,
  Contact,
  HandCoins,
  ListChecks,
  LogOut,
  MessageSquareText,
  MonitorSmartphone,
  QrCode,
  Receipt,
  Settings,
  Share,
  TriangleAlert,
  UserRound,
} from 'lucide-react'
import { StrictMode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import {
  type Api,
  auth,
  createApi,
  dangKyKhiPhienChet,
  docToken,
  luuToken,
  type ToiLaAi,
} from './api.ts'
import { CauHinh, type TheCauHinh } from './CauHinh.tsx'
import { NganThongBao, NutChuong, useChuong } from './Chuong.tsx'
import { quenCaiDat } from './cai-dat-tiem.ts'
import { dichCuaTin, laIdChiNhanh } from './chi-nhanh-dang-dung.ts'
import { ChiNhanhCtx, useChiNhanhGoc } from './chi-nhanh-dang-dung.tsx'
import { DangNhap, DoiMa } from './DangNhap.tsx'
import { TrangDanhSach } from './DanhSach.tsx'
import { TrangLich } from './DayBoard.tsx'
import { nhoTenTiem } from './dang-nhap-nho.ts'
import { DaiBaoHan, DaiDieuKhoan } from './GoiSapa.tsx'
import { TrangKhach } from './GoiVi.tsx'
import { huongKhiKiemPhienLoi } from './kiem-phien.ts'
import { LienHeSapaKhoi } from './LienHeSapa.tsx'
import { apMauQuay, boMauQuay } from './mau-quay.ts'
import { docVuaMoTiem, xoaVuaMoTiem } from './mo-tiem-ma.ts'
import { ToPhanHoi } from './PhanHoi.tsx'
import { khoiDongPwa, useCaiDat } from './pwa.ts'
import { ToQrSapa } from './QrSapa.tsx'
import { TrangCuaToi, TrangSoTien } from './SoTien.tsx'
import { S } from './text/so-tien.ts'
import './styles.css'
import { chonMucDai } from './dai-canh-bao-chu.ts'
import { CHU_KY_LAM_MOI_DAI_MS, nenLamMoiDai } from './lam-moi-dai.ts'
import { G } from './text/goi-vi.ts'
import { PH } from './text/phan-hoi.ts'
import { QS } from './text/qr-sapa.ts'
import { Q } from './text/quen-ma.ts'
import { datNganh, tenVaiHienThi } from './text/theo-nganh.ts'
import { TB } from './text/thong-bao.ts'
import { T } from './text/vi.ts'
import { Avatar, DaiBanMoi, khoaCuonTrang, Logo, RaoLoi, ThongBao } from './ui.tsx'

function App() {
  /** `null` = chưa biết (đang kiểm token cũ). `false` = chưa đăng nhập. */
  const [toi, setToi] = useState<ToiLaAi | null | false>(null)
  const [hetPhien, setHetPhien] = useState(false)
  /** Không tới được máy chủ lúc mở app: giữ phiên, hiện "Thử lại" — không đẩy người dùng ra màn đăng nhập. */
  const [chapMang, setChapMang] = useState(false)

  const kiemPhien = useCallback(() => {
    if (docToken() === null) {
      setToi(false)
      return
    }
    setChapMang(false)
    void auth
      .toi()
      .then((t) => {
        // Lời chào lần sau gọi đúng tên tiệm, không phải mã tiệm.
        nhoTenTiem({ id: t.tiem, ma: t.maTiem }, t.tenTiem)
        setToi(t)
      })
      .catch((loi: unknown) => {
        if (huongKhiKiemPhienLoi(loi) === 'thu-lai') {
          setChapMang(true)
          return
        }
        // Token cũ không còn dùng được: về màn đăng nhập, không hiện lỗi đỏ.
        setToi(false)
      })
  }, [])

  useEffect(() => {
    kiemPhien()
    // Tầng gọi API báo về khi máy chủ trả 401: quay ngay về màn đăng nhập.
    dangKyKhiPhienChet(() => {
      setToi(false)
      setHetPhien(true)
    })
    return () => dangKyKhiPhienChet(null)
  }, [kiemPhien])

  if (toi === null && chapMang) {
    return (
      <div className="man-cho man-cho-loi" role="alert">
        <Logo co={48} />
        <p className="man-cho-chu">{T.khongToiDuocMayChu}</p>
        <button type="button" className="nut nut-chinh nut-lon" onClick={kiemPhien}>
          {T.thuLai}
        </button>
      </div>
    )
  }
  if (toi === null) {
    return (
      <div className="man-cho">
        <Logo co={48} />
      </div>
    )
  }
  if (toi === false) {
    return (
      <DangNhap
        hetPhien={hetPhien}
        onXong={() => {
          setHetPhien(false)
          kiemPhien()
        }}
      />
    )
  }
  if (toi.toi.phaiDoiMa) return <DoiMa onXong={kiemPhien} />
  // 4.3: chữ theo nhóm ngành (Nhân viên / Kỹ thuật viên…) — đặt TRƯỚC khi vẽ; đổi ngành → tải lại `toi` → vẽ lại.
  datNganh(toi.nganh)
  return <BanLamViec toi={toi} onDangXuat={() => setToi(false)} onTaiLaiToi={kiemPhien} />
}

type Trang = 'lich' | 'danh-sach' | 'khach' | 'so-tien' | 'cua-toi' | 'cau-hinh'

function BanLamViec({
  toi,
  onDangXuat,
  onTaiLaiToi,
}: {
  toi: ToiLaAi
  onDangXuat: () => void
  onTaiLaiToi: () => void
}) {
  // v0.23.0 (ADR-115): web quầy mang màu của NGÀNH sau đăng nhập. Layout effect = trước khi vẽ nên không nháy xanh SAPA;
  // đổi ngành ở Thông tin tiệm → `toi.nganh` đổi → áp lại, không tải lại trang. Gỡ khỏi màn (đăng xuất / hết phiên) → về
  // xanh SAPA ở màn đăng nhập. Màn chờ, đăng nhập, buộc đổi mã nằm NGOÀI `BanLamViec` nên giữ xanh SAPA.
  useLayoutEffect(() => {
    apMauQuay(toi.nganh)
    return () => boMauQuay()
  }, [toi.nganh])
  const choCauHinh = toi.quyen.includes('cau-hinh.tiem')
  const laTho = toi.toi.vai === 'technician'
  const tenVai = tenVaiHienThi(toi.toi.vai, toi.toi.tenVai)
  // Thợ mở máy là thấy việc của mình hôm nay, không phải bảng lịch cả tiệm.
  const [trang, setTrang] = useState<Trang>(laTho ? 'cua-toi' : 'lich')
  // Vào bàn làm việc (vừa đăng nhập, bàn phím vừa đóng) và mỗi lần đổi tab: trang về đầu. Không thì trang giữ vị trí cuộn cũ
  // và đầu trang dính che mất ô chọn ngày của Lịch (iPhone, 16/10).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `trang` là TÍN HIỆU chạy lại (đổi tab → về đầu trang).
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [trang])
  /** Ngày màn lịch mở ra, và khoá để dựng lại màn lịch khi nhảy từ danh sách sang. */
  const [ngayLich, setNgayLich] = useState<{
    ngay: string
    lan: number
    /** BK-2: từ cảnh báo "lịch vướng" ở Cấu hình — mở tờ "Xử lý" (chi nhánh của lịch vướng đã đặt qua `doi(…, {nho:false})`). */
    xuLy?: boolean
    /** Từ chuông: lịch được mở luôn tờ chi tiết khi bảng ngày tải xong (chi nhánh của tin đã đặt qua `denChiNhanhCuaTin`). */
    lich?: string
  } | null>(null)
  /** Số điện thoại mang từ trang Khách sang tờ đặt lịch (một lần). */
  const [sdtDat, setSdtDat] = useState<{ sdt: string; lan: number } | null>(null)
  /** KH-GĐ2 7.2/7.3: bấm tin "hoá đơn bị huỷ" / "két lệch" trong chuông → mở Sổ tiền đúng thẻ, đúng ngày. */
  const [soTienDau, setSoTienDau] = useState<{
    the: 'huy' | 'ket'
    ngay: string
    lan: number
  } | null>(null)
  const [theCauHinh, setTheCauHinh] = useState<TheCauHinh>('chi-nhanh')
  /** Bấm "Khai tài khoản nhận tiền" ở tờ thu → mở Cài đặt tiệm rồi cuộn tới thẻ tài khoản MỘT lần (thẻ báo xong thì về `false`). */
  const [cuonTaiKhoan, setCuonTaiKhoan] = useState(false)
  const [moTaiKhoan, setMoTaiKhoan] = useState(false)
  /** Tờ "việc với SAPA" mở từ menu tài khoản (QR, góp ý). Đóng tờ → focus về nút đã mở menu. */
  const [toSapa, setToSapa] = useState<null | 'qr' | 'gop-y'>(null)
  const daMoToSapa = useRef(false)
  /** Vừa gửi góp ý xong: thông báo nổi 6 giây (hoặc bấm đóng). */
  const [daGuiGopY, setDaGuiGopY] = useState(false)
  useEffect(() => {
    if (!daGuiGopY) return
    const hen = setTimeout(() => setDaGuiGopY(false), 6000)
    return () => clearTimeout(hen)
  }, [daGuiGopY])
  /** Menu tài khoản (vòng 46): focus vào menu khi mở, Escape đóng, đóng xong trả focus về nút đã mở. */
  const menuRef = useRef<HTMLDivElement>(null)
  const nutMoRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!moTaiKhoan) return
    menuRef.current?.focus()
    const khiBam = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoTaiKhoan(false)
    }
    document.addEventListener('keydown', khiBam)
    // Khoá cuộn thân trang khi menu mở (vòng 48); nhả ở MỌI đường đóng (nút Đóng, lớp phủ, Escape, gỡ màn).
    const nha = khoaCuonTrang()
    return () => {
      document.removeEventListener('keydown', khiBam)
      nha()
      nutMoRef.current?.focus()
    }
  }, [moTaiKhoan])
  useEffect(() => {
    if (toSapa !== null) {
      daMoToSapa.current = true
      return
    }
    if (daMoToSapa.current) {
      daMoToSapa.current = false
      nutMoRef.current?.focus()
    }
  }, [toSapa])
  /**
   * Vừa mở tiệm xong (ADR-087): câu "Lần sau đăng nhập bằng số điện thoại và mã bạn vừa đặt" nằm ở ĐẦU THẺ
   * "Chuẩn bị tiệm" cho tới khi chủ bấm một bước hoặc đóng — không còn là toast 10 giây dễ bỏ lỡ (vòng sửa UX Đợt 1).
   */
  const [chaoMoTiem, setChaoMoTiem] = useState(() => docVuaMoTiem())
  const dongChao = useCallback(() => {
    setChaoMoTiem(false)
    xoaVuaMoTiem()
  }, [])
  /** Tăng khi thoát khỏi màn lỗi để dựng lại trang từ đầu. */
  const [lanRao, setLanRao] = useState(0)
  const [api] = useState<Api>(() => createApi(toi.tiem))
  /** ADR-101: MỘT chi nhánh đang dùng cho cả máy — mọi màn đọc qua `useChiNhanh()`, không tự lấy chi nhánh đầu. */
  const cn = useChiNhanhGoc(api, toi)
  // Chủ vừa thêm / ẩn chi nhánh ở Cấu hình: rời trang đó là tải lại danh sách.
  const trangTruoc = useRef<Trang>(trang)
  const { taiLai: taiLaiChiNhanh } = cn
  useEffect(() => {
    if (trangTruoc.current === 'cau-hinh' && trang !== 'cau-hinh') taiLaiChiNhanh()
    trangTruoc.current = trang
  }, [trang, taiLaiChiNhanh])
  const caiDat = useCaiDat()
  const chuong = useChuong(api, toi)
  const [moChuong, setMoChuong] = useState(false)

  // Làm mới dải (B-V2, QV2): tablet quầy để mở cả ngày không bao giờ tải lại `/v1/auth/toi`, nên dải cảnh báo chỉ cập nhật ở đây.
  // State RIÊNG chỉ cho ba trường của dải — KHÔNG `setToi` cả đối tượng (chạy lại các effect phụ thuộc `toi`, đóng tờ đang mở) và
  // lỗi mạng / 5xx BỎ QUA (khác `kiemPhien`: không đặt `toi` về false, không nháy màn tải). 401 vẫn đi đường `dangKyKhiPhienChet` sẵn có.
  const [daiMoi, setDaiMoi] = useState<{
    datLaiMa: NonNullable<ToiLaAi['datLaiMa']>
    doiChu: ToiLaAi['doiChuGanDay']
    doiQuanLy: ToiLaAi['doiQuanLyGanDay']
  } | null>(null)
  const lanDocDaiCuoi = useRef(Date.now()) // gio-that: hẹn giờ giao diện, không phải mốc nghiệp vụ
  // `toi` mới (vừa đăng nhập / tải lại sau Thông tin tiệm) đã mang dải mới nhất: bỏ bản làm mới cũ.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `toi` là TÍN HIỆU chạy lại (đổi đối tượng → dùng dữ liệu của nó).
  useEffect(() => {
    setDaiMoi(null)
    lanDocDaiCuoi.current = Date.now() // gio-that: hẹn giờ giao diện
  }, [toi])
  useEffect(() => {
    let conSong = true
    let dangGoi = false
    const lamMoi = () => {
      if (dangGoi) return
      dangGoi = true
      void auth
        .toi()
        .then((t) => {
          if (!conSong) return
          lanDocDaiCuoi.current = Date.now() // gio-that: hẹn giờ giao diện
          setDaiMoi({
            datLaiMa: t.datLaiMa ?? [],
            doiChu: t.doiChuGanDay,
            doiQuanLy: t.doiQuanLyGanDay,
          })
        })
        .catch(() => {
          // Mạng chập chờn / máy chủ 5xx: giữ dải đang hiện, lần quay lại kế tiếp thử lại.
        })
        .finally(() => {
          dangGoi = false
        })
    }
    const kiemLai = () => {
      if (
        nenLamMoiDai({
          lanCuoiMs: lanDocDaiCuoi.current,
          bayGioMs: Date.now(), // gio-that: hẹn giờ giao diện
          dangHien: document.visibilityState === 'visible',
        })
      )
        lamMoi()
    }
    document.addEventListener('visibilitychange', kiemLai)
    const hen = window.setInterval(kiemLai, CHU_KY_LAM_MOI_DAI_MS)
    return () => {
      conSong = false
      document.removeEventListener('visibilitychange', kiemLai)
      window.clearInterval(hen)
    }
  }, [])
  // Hết làm mới dải
  /** Trang Khách mở thẳng thẻ Chờ chỗ (từ chuông) — `lan` để dựng lại khi bấm lần nữa. */
  const [theKhach, setTheKhach] = useState<{ the: 'cho'; lan: number } | null>(null)

  /** Chuông / tin đẩy vừa tự chuyển chi nhánh (chỉ tạm): báo 6 giây, kèm nút quay về chi nhánh cũ. */
  const [daChuyen, setDaChuyen] = useState<{
    denId: string
    tuId: string
    tuTen: string
    denTen: string
  } | null>(null)
  useEffect(() => {
    if (daChuyen === null) return
    const hen = setTimeout(() => setDaChuyen(null), 6000)
    return () => clearTimeout(hen)
  }, [daChuyen])
  // Người dùng tự đổi chi nhánh lúc báo còn hiện: báo cũ không còn đúng nữa.
  useEffect(() => {
    if (daChuyen !== null && cn.id !== daChuyen.denId) setDaChuyen(null)
  }, [cn.id, daChuyen])

  /**
   * Bấm tin của chi nhánh khác (chuông, tin đẩy): chuyển TẠM sang chi nhánh đó — KHÔNG ghi nhớ (tải lại là về chi nhánh đã nhớ,
   * cùng BK-2). Phải gọi TRƯỚC khi đặt ngày / trang để màn mới dựng thẳng ở chi nhánh đúng, không nháy chi nhánh cũ.
   * Chi nhánh không chọn được (đã ẩn / không được giao) hoặc tiệm một chi nhánh: không đổi gì, đi như trước.
   */
  function denChiNhanhCuaTin(branchId: string) {
    const d = dichCuaTin(branchId, cn)
    if (d.loai === 'cho') {
      // Danh sách chi nhánh chưa tải xong (bấm tin quá sớm): chưa biết đổi được không → giữ lựa chọn, áp lại khi `cn.ds` về.
      cnCho.current = branchId
      return
    }
    if (d.loai !== 'doi') return
    cn.doi(d.id, { nho: false })
    setDaChuyen({ denId: d.id, tuId: cn.id, tuTen: d.tuTen, denTen: d.denTen })
  }

  // Chi nhánh đang CHỜ áp khi danh sách chi nhánh tải xong: từ tin web quầy bấm quá sớm, hoặc từ tin Web Push
  // (service worker mở `/?mo=cho[&cn=<id>]` hoặc `/?mo=thong-bao`). `cn` trên đường dẫn là đầu vào KHÔNG tin cậy: kiểm dạng id,
  // giữ tới khi danh sách tải xong, rồi đi qua `dichCuaTin` (chỉ nhận id có trong danh sách chọn được; id rác / chi nhánh
  // đã ẩn → bỏ qua), không ghi nhớ.
  const cnCho = useRef<string | null>(null)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    const mo = p.get('mo')
    if (mo === null) return
    window.history.replaceState(null, '', window.location.pathname)
    if (mo === 'cho' && toi.quyen.includes('dat-lich')) {
      const c = p.get('cn')
      if (laIdChiNhanh(c)) cnCho.current = c
      setTheKhach({ the: 'cho', lan: 1 })
      setTrang('khach')
    } else if (mo === 'thong-bao') {
      setMoChuong(true)
    }
  }, [toi])
  // biome-ignore lint/correctness/useExhaustiveDependencies: `cn` là TÍN HIỆU (danh sách chi nhánh vừa tải xong); `denChiNhanhCuaTin` luôn đọc `cn` mới nhất.
  useEffect(() => {
    const id = cnCho.current
    if (id === null || cn.ds === null) return
    cnCho.current = null
    denChiNhanhCuaTin(id)
  }, [cn])

  function moCauHinh(the: TheCauHinh) {
    setTheCauHinh(the)
    setTrang('cau-hinh')
  }

  /** Nút "Khai tài khoản nhận tiền" ở tờ thu (chỉ chủ): mở Cấu hình → Cài đặt tiệm và cuộn tới thẻ "Tài khoản nhận tiền" một lần. */
  function moKhaiTaiKhoan() {
    setCuonTaiKhoan(true)
    moCauHinh('cai-dat')
  }

  function dangXuat() {
    // Thu hồi phiên ở máy chủ trước, rồi mới xoá ở máy này: nếu chỉ xoá ở máy này thì token vẫn
    // sống, và cái tablet bị mất vẫn đang đăng nhập.
    void auth
      .dangXuat()
      .catch(() => undefined)
      .finally(() => {
        luuToken(null)
        // Lời chào "vừa mở tiệm" thuộc về tiệm này, không sang người đăng nhập sau.
        xoaVuaMoTiem()
        // Người đăng nhập sau có thể ở tiệm khác: không được thấy chế độ gọn của tiệm này.
        quenCaiDat()
        onDangXuat()
      })
  }

  const cacMuc: { ma: Trang; ten: string; icon: typeof CalendarDays }[] = [
    // KTV: việc hôm nay + hoa hồng tháng này (chỉ đọc, ADR-046) — mục ĐẦU TIÊN, là trang đích.
    ...(laTho ? [{ ma: 'cua-toi' as const, ten: S.navCuaToi, icon: HandCoins }] : []),
    { ma: 'lich', ten: laTho ? T.navMyCalendar : T.navCalendar, icon: CalendarDays },
    { ma: 'danh-sach', ten: T.navList, icon: ListChecks },
    // Bán gói / nạp ví cho khách vãng lai (không đi từ một lịch hẹn) — chỉ vai thu tiền.
    ...(toi.quyen.includes('thu-tien')
      ? [
          { ma: 'khach' as const, ten: G.navCustomer, icon: Contact },
          { ma: 'so-tien' as const, ten: S.nav, icon: Receipt },
        ]
      : []),
    ...(choCauHinh ? [{ ma: 'cau-hinh' as const, ten: T.navConfig, icon: Settings }] : []),
  ]

  /*
   * §7 (đợt 3 · 3.3): nhãn thanh dưới ≥ 16px nên chỉ vừa ~5 nhãn ở 360px. Vòng 44: nút "Cấu hình" mở THẲNG trang
   * Cấu hình (vòng 42 mở menu tài khoản mà nhãn ghi "Cấu hình" — nhãn hứa một nơi, nút giao nơi khác). Tài khoản
   * (tên + vai, Gọi / nhắn SAPA, cài ứng dụng, Đăng xuất) có nút riêng là ảnh đại diện ở góc trên phải mọi màn điện
   * thoại → 2 chạm từ mọi màn, mọi vai; thanh dưới chỉ còn là các TRANG.
   * Chỉ một ca hẹp: thợ có cả mục thu tiền (5 nhãn, hai nhãn dài "Việc của tôi / Lịch của tôi") — "Danh sách" xuống
   * menu tài khoản như vòng 42; thợ chỉ có ba mục thì "Danh sách" nằm ngay trên thanh.
   */
  const trongTaiKhoan = (ma: Trang) => laTho && ma === 'danh-sach' && cacMuc.length > 3
  const cacMucDuoi = cacMuc.filter((m) => !trongTaiKhoan(m.ma))
  const cacMucTrongTaiKhoan = cacMuc.filter((m) => trongTaiKhoan(m.ma))

  return (
    <ChiNhanhCtx.Provider value={cn}>
      <div className="khung">
        <nav className="thanh-ben" aria-label={T.brand}>
          <div className="logo">
            <Logo />
          </div>
          {cacMuc.map((m) => (
            <button
              key={m.ma}
              type="button"
              className={`muc-dh ${trang === m.ma ? 'dang-chon' : ''}`}
              aria-current={trang === m.ma ? 'page' : undefined}
              onClick={() => {
                setSdtDat(null)
                setTheKhach(null)
                setTrang(m.ma)
              }}
            >
              <m.icon size={24} strokeWidth={1.9} />
              {m.ten}
            </button>
          ))}
          <div className="khoang-trong" />
          <NutChuong soMoi={chuong.soMoi} onMo={() => setMoChuong(true)} kieu="thanh-ben" />
          <button
            type="button"
            className="muc-dh"
            aria-label={`${T.accountMenu} · ${tenVai}`}
            onClick={(e) => {
              nutMoRef.current = e.currentTarget
              setMoTaiKhoan(true)
            }}
          >
            <Avatar ten={toi.toi.ten} />
            <span className="nhan-vai">{tenVai}</span>
          </button>
        </nav>

        <div className="noi-dung">
          {/* Có bản mới của web quầy: dòng đầu, mọi vai, trong luồng trang (docs/plans/nut-cap-nhat-ban-moi-web-quay.md). */}
          <DaiBanMoi />
          {/* Chủ tiệm: dải báo khi gói SAPA sắp / đã hết hạn (phản ánh 25/09 đợt 4). */}
          {toi.quyen.includes('xem.goi-sapa') && <DaiBaoHan onMo={() => moCauHinh('goi-sapa')} />}
          {/* 5.4: chủ tiệm có sẵn — dải "SAPA cập nhật điều khoản" tới khi bấm Đồng ý (cờ tắt = không hiện). */}
          {toi.quyen.includes('xem.goi-sapa') && <DaiDieuKhoan />}
          <DaiDatLaiMa
            ds={daiMoi?.datLaiMa ?? toi.datLaiMa ?? []}
            doiChu={daiMoi === null ? toi.doiChuGanDay : daiMoi.doiChu}
            doiQuanLy={daiMoi === null ? toi.doiQuanLyGanDay : daiMoi.doiQuanLy}
          />
          {/* Mỗi trang có rào lỗi riêng: trang hỏng vẫn còn thanh điều hướng, bấm "Về Lịch hẹn" là thoát. */}
          <RaoLoi
            key={`${trang}-${lanRao}`}
            khiVe={() => {
              setLanRao((x) => x + 1)
              setTrang(laTho ? 'cua-toi' : 'lich')
            }}
          >
            {trang === 'lich' ? (
              <TrangLich
                key={`${ngayLich === null ? 'hom-nay' : `${ngayLich.ngay}#${ngayLich.lan}`}|${sdtDat?.lan ?? 0}`}
                toi={toi}
                api={api}
                onMoCauHinh={moCauHinh}
                chaoMoTiem={chaoMoTiem}
                onDongChao={dongChao}
                {...(ngayLich === null ? {} : { ngayDau: ngayLich.ngay })}
                {...(ngayLich?.xuLy === true
                  ? {
                      moXuLyDau: true,
                      // Mở một lần: quay lại Lịch sau đó không bật lại tờ "Xử lý".
                      onDaMoXuLyDau: () =>
                        setNgayLich((cu) => (cu === null ? cu : { ngay: cu.ngay, lan: cu.lan })),
                    }
                  : {})}
                {...(ngayLich?.lich === undefined ? {} : { lichDau: ngayLich.lich })}
                {...(sdtDat === null ? {} : { sdtDau: sdtDat.sdt })}
                {...(toi.quyen.includes('cau-hinh.nhan-vien')
                  ? { onKhaiTaiKhoan: moKhaiTaiKhoan }
                  : {})}
              />
            ) : trang === 'khach' ? (
              <TrangKhach
                key={theKhach === null ? 'khach' : `cho#${theKhach.lan}`}
                toi={toi}
                api={api}
                {...(theKhach === null ? {} : { theDau: theKhach.the })}
                {...(toi.quyen.includes('cau-hinh.tiem')
                  ? { onMoDichVu: () => moCauHinh('dich-vu') }
                  : {})}
                {...(toi.quyen.includes('dat-lich')
                  ? {
                      onDatLich: (sdt: string) => {
                        setSdtDat((cu) => ({ sdt, lan: (cu?.lan ?? 0) + 1 }))
                        setTrang('lich')
                      },
                    }
                  : {})}
              />
            ) : trang === 'so-tien' ? (
              <TrangSoTien
                key={soTienDau === null ? 'so-tien' : `so-tien#${soTienDau.lan}`}
                toi={toi}
                api={api}
                {...(soTienDau === null ? {} : { theDau: soTienDau.the, ngayDau: soTienDau.ngay })}
                {...(choCauHinh ? { onMoCauHinhSp: () => moCauHinh('san-pham') } : {})}
              />
            ) : trang === 'cua-toi' ? (
              <TrangCuaToi toi={toi} api={api} />
            ) : trang === 'danh-sach' ? (
              <TrangDanhSach
                toi={toi}
                api={api}
                onXemTrenLich={(ngay) => {
                  setNgayLich((cu) => ({ ngay, lan: (cu?.lan ?? 0) + 1 }))
                  setTrang('lich')
                }}
                {...(toi.quyen.includes('cau-hinh.nhan-vien')
                  ? { onKhaiTaiKhoan: moKhaiTaiKhoan }
                  : {})}
              />
            ) : (
              <CauHinh
                toi={toi}
                the={theCauHinh}
                onDoiThe={setTheCauHinh}
                laChuTiem={toi.quyen.includes('cau-hinh.nhan-vien')}
                cuonTaiKhoan={cuonTaiKhoan}
                onDaCuonTaiKhoan={() => setCuonTaiKhoan(false)}
                onDoiThongTin={onTaiLaiToi}
                {...(toi.quyen.includes('dat-lich')
                  ? {
                      // BK-2 (ADR-096): đổi ca / nghỉ việc / giờ mở cửa còn lịch vướng → bấm lịch mở thẳng Lịch + tờ "Xử lý".
                      onXemTrenLich: (ngay: string, chiNhanh: string) => {
                        // Chi nhánh của lịch vướng chỉ là TẠM (không phải người dùng chọn): không ghi nhớ — tải lại là về chi nhánh đã nhớ.
                        if (cn.nhieu && cn.ds?.some((c) => c.id === chiNhanh) === true)
                          cn.doi(chiNhanh, { nho: false })
                        setNgayLich((cu) => ({
                          ngay,
                          lan: (cu?.lan ?? 0) + 1,
                          xuLy: true,
                        }))
                        setTrang('lich')
                      },
                    }
                  : {})}
                {...(toi.quyen.includes('dat-lich')
                  ? {
                      // Mở thẳng tờ đặt lịch (cùng cách nút "Đặt lịch" từ Khách): DayBoard mở tờ khi có `sdtDau`.
                      onVeLich: () => {
                        setSdtDat((cu) => ({ sdt: '', lan: (cu?.lan ?? 0) + 1 }))
                        setTrang('lich')
                      },
                    }
                  : {})}
              />
            )}
          </RaoLoi>
        </div>

        {/* Điện thoại: chuông + tài khoản nổi góc trên phải (thanh dưới chỉ chứa các trang). */}
        <NutChuong soMoi={chuong.soMoi} onMo={() => setMoChuong(true)} kieu="noi" />
        {/* Điện thoại: tài khoản ngay cạnh chuông (tablet có ảnh đại diện ở thanh bên); menu mở ngay dưới nút. */}
        <button
          type="button"
          className={`nut-tai-khoan-noi ${moTaiKhoan ? 'dang-chon' : ''}`}
          aria-label={`${T.accountMenu} · ${tenVai}`}
          aria-haspopup="dialog"
          aria-expanded={moTaiKhoan}
          onClick={(e) => {
            nutMoRef.current = e.currentTarget
            setMoTaiKhoan(true)
          }}
        >
          {/* Biểu tượng người (vòng 46): chữ cái đầu của tên không ai đọc ra là "tài khoản". */}
          <UserRound size={24} strokeWidth={1.9} aria-hidden="true" />
        </button>

        {moChuong && (
          <RaoLoi khiDong={() => setMoChuong(false)}>
            <NganThongBao
              api={api}
              chuong={chuong}
              onDong={() => setMoChuong(false)}
              onMoLich={(ngay, branchId, lichId) => {
                setMoChuong(false)
                denChiNhanhCuaTin(branchId)
                setNgayLich((cu) => ({
                  ngay,
                  lan: (cu?.lan ?? 0) + 1,
                  ...(lichId === null ? {} : { lich: lichId }),
                }))
                setSdtDat(null)
                setTrang('lich')
              }}
              onMoCho={(branchId) => {
                setMoChuong(false)
                denChiNhanhCuaTin(branchId)
                setTheKhach((cu) => ({ the: 'cho', lan: (cu?.lan ?? 0) + 1 }))
                setTrang('khach')
              }}
              onMoSoTien={(the, ngay, branchId) => {
                setMoChuong(false)
                // "Bị huỷ" là màn cả tiệm (không kèm chi nhánh); Két thì theo chi nhánh của tin.
                if (branchId !== undefined) denChiNhanhCuaTin(branchId)
                setSoTienDau((cu) => ({ the, ngay, lan: (cu?.lan ?? 0) + 1 }))
                setTrang('so-tien')
              }}
            />
          </RaoLoi>
        )}

        <nav className="thanh-duoi" aria-label={T.brand}>
          {cacMucDuoi.map((m) => (
            <button
              key={m.ma}
              type="button"
              className={`muc-dh ${trang === m.ma && !moTaiKhoan ? 'dang-chon' : ''}`}
              aria-current={trang === m.ma ? 'page' : undefined}
              onClick={() => {
                setSdtDat(null)
                setTheKhach(null)
                setTrang(m.ma)
              }}
            >
              <m.icon size={24} strokeWidth={1.9} />
              {m.ten}
            </button>
          ))}
        </nav>

        {moTaiKhoan && (
          <>
            <div className="man-che" onClick={() => setMoTaiKhoan(false)} aria-hidden="true" />
            <div
              ref={menuRef}
              className="the menu-tai-khoan"
              role="dialog"
              aria-modal="true"
              aria-label={T.accountMenu}
              tabIndex={-1}
            >
              <div className="ai">
                <Avatar ten={toi.toi.ten} co="lon" />
                <div>
                  <b>{toi.toi.ten}</b>
                  <span>{tenVai}</span>
                  <span>{toi.tenTiem}</span>
                </div>
              </div>
              {/* Mục không đủ chỗ ở thanh dưới của điện thoại (xem `trongTaiKhoan`, hiếm); tablet có đủ ở thanh bên. */}
              {cacMucTrongTaiKhoan.map((m) => (
                <button
                  key={m.ma}
                  type="button"
                  className={`nut nut-vien nut-rong chi-dien-thoai ${trang === m.ma ? 'dang-chon' : ''}`}
                  aria-current={trang === m.ma ? 'page' : undefined}
                  onClick={() => {
                    setMoTaiKhoan(false)
                    setSdtDat(null)
                    setTheKhach(null)
                    setTrang(m.ma)
                  }}
                >
                  <m.icon size={18} />
                  {m.ten}
                </button>
              ))}
              {/* Chủ / quản lý / lễ tân có nhận khách: xem việc + hoa hồng của mình (thợ có ở menu chính). */}
              {!laTho && toi.toi.lamDichVu === true && (
                <button
                  type="button"
                  className="nut nut-vien nut-rong"
                  onClick={() => {
                    setMoTaiKhoan(false)
                    setTrang('cua-toi')
                  }}
                >
                  <HandCoins size={18} />
                  {S.navCuaToi}
                </button>
              )}
              {caiDat.cach === 'nut' && (
                <div className="cai-dat">
                  <button
                    type="button"
                    className="nut nut-chinh nut-rong"
                    onClick={() => void caiDat.cai()}
                  >
                    <MonitorSmartphone size={18} />
                    {T.installApp}
                  </button>
                  <p className="chu-phu">{T.installHint}</p>
                </div>
              )}
              {caiDat.cach === 'ios' && (
                <p className="cai-dat cai-dat-ios">
                  <Share size={18} />
                  {T.installIos}
                </p>
              )}
              {/* Gặp sự cố giữa giờ làm: 2 chạm (ảnh đại diện → Gọi SAPA), mọi màn, mọi vai (đợt 3 · 3.2, vòng 44). */}
              <LienHeSapaKhoi kieu="menu" />
              <div className="menu-sapa-hang">
                <button
                  type="button"
                  className="nut nut-vien"
                  onClick={() => {
                    setMoTaiKhoan(false)
                    setToSapa('qr')
                  }}
                >
                  <QrCode size={18} />
                  {QS.nutMo}
                </button>
                <button
                  type="button"
                  className="nut nut-vien"
                  onClick={() => {
                    setMoTaiKhoan(false)
                    setToSapa('gop-y')
                  }}
                >
                  <MessageSquareText size={18} />
                  {PH.nutMo}
                </button>
              </div>
              <button type="button" className="nut nut-vien nut-rong" onClick={dangXuat}>
                <LogOut size={18} />
                {T.logout}
              </button>
              <button
                type="button"
                className="nut nut-vien nut-rong nut-dong-menu"
                onClick={() => setMoTaiKhoan(false)}
              >
                {T.close}
              </button>
            </div>
          </>
        )}
        {toSapa === 'qr' && <ToQrSapa onDong={() => setToSapa(null)} />}
        {toSapa === 'gop-y' && (
          <ToPhanHoi
            man={trang}
            onDong={() => setToSapa(null)}
            onDaGui={() => {
              setToSapa(null)
              setDaGuiGopY(true)
            }}
          />
        )}
        {daGuiGopY && (
          <ThongBao loai="thanh-cong" noi lon onDong={() => setDaGuiGopY(false)}>
            {PH.daGui}
          </ThongBao>
        )}
        {daChuyen !== null && (
          <ThongBao
            loai="thanh-cong"
            noi
            lon
            lop="bao-chuyen-cn"
            onDong={() => setDaChuyen(null)}
            hanhDong={
              <button
                type="button"
                className="nut nut-nhat nut-hoan-tac"
                onClick={() => {
                  // Về chi nhánh cũ cũng chỉ là tạm: chi nhánh đã nhớ trên máy không đổi.
                  cn.doi(daChuyen.tuId, { nho: false })
                  setDaChuyen(null)
                }}
              >
                {TB.veChiNhanh(daChuyen.tuTen)}
              </button>
            }
          >
            {TB.daChuyenSang(daChuyen.denTen)}
          </ThongBao>
        )}
      </div>
    </ChiNhanhCtx.Provider>
  )
}

khoiDongPwa()

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Khong tim thay #root')
createRoot(rootEl).render(
  <StrictMode>
    <RaoLoi>
      <App />
    </RaoLoi>
  </StrictMode>,
)

const KHOA_DA_BIET = 'sapa.daBietDatLaiMa'

/**
 * Dải báo "mã của chủ tiệm vừa được đặt lại qua Zalo / bởi SAPA" (ADR-072): kẻ gian lừa được mã Zalo thì người
 * thật trong tiệm vẫn biết. "Đã biết" chỉ ẩn trên máy này.
 */
function DaiDatLaiMa({
  ds,
  doiChu,
  doiQuanLy,
}: {
  ds: NonNullable<ToiLaAi['datLaiMa']>
  /** v0.23.1 (ADR-123): việc NGƯỜI TRONG TIỆM làm tới một chủ (`toi.doiChuGanDay`); máy chủ cũ không trả. */
  doiChu: ToiLaAi['doiChuGanDay']
  /** v0.23.2 (B-V2): việc tới Quản lý / Lễ tân (`toi.doiQuanLyGanDay`); máy chủ cũ không trả. */
  doiQuanLy: ToiLaAi['doiQuanLyGanDay']
}) {
  const [daBiet, setDaBiet] = useState<string[]>(() => {
    try {
      const v: unknown = JSON.parse(localStorage.getItem(KHOA_DA_BIET) ?? '[]')
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
    } catch {
      return []
    }
  })
  // Zalo / SAPA trước, rồi việc trong tiệm chạm tới chủ (ADR-123); một mục chưa "Đã biết" mỗi lần.
  const mot = chonMucDai({ datLaiMa: ds, doiChu, doiQuanLy, daBiet })
  if (mot === null) return null
  return (
    // QV3: Zalo / SAPA (từ ngoài) ĐỎ; việc người trong tiệm làm VÀNG (`trong-tiem` không có luật riêng → vàng mặc định của dải).
    <div
      className="dai-bao-han"
      data-muc={mot.nguon === 'zalo-sapa' ? 'da-het' : 'trong-tiem'}
      role="status"
    >
      <TriangleAlert size={18} />
      <span>
        {mot.cau} {mot.laToi ? Q.daiKhongPhaiBan : Q.daiKhongPhai}
      </span>
      <button
        type="button"
        className="nut-lien-ket"
        onClick={() => {
          const moi = [...daBiet, mot.id].slice(-50)
          setDaBiet(moi)
          try {
            localStorage.setItem(KHOA_DA_BIET, JSON.stringify(moi))
          } catch {
            // Không lưu được thì lần sau hiện lại. Không sao.
          }
        }}
      >
        {Q.daiDaBiet}
      </button>
    </div>
  )
}
